import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { brand } from '../../config/brand';
import { avatarColor } from '../ui/Avatar';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const IconDashboard = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
  </svg>
);

const IconUsers = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

const IconWorkspace = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
  </svg>
);

const IconKey = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
  </svg>
);

const IconLogout = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
  </svg>
);

const IconChevron = ({ open }: { open: boolean }) => (
  <svg
    className="sidebar-icon-svg"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
    style={{ width: 14, height: 14, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);

const IconSun = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
  </svg>
);

const IconMoon = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
  </svg>
);

const IconCollapse = ({ collapsed }: { collapsed: boolean }) => (
  <svg
    className="sidebar-icon-svg"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
    style={{ width: 16, height: 16, transition: 'transform 0.2s', transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const IconOffers = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const IconInvoices = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3v18h18V3H3zm4 4h10M7 11h10M7 15h6M16 19l3-3-3-3" />
  </svg>
);

const IconRecurring: React.FC = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 4v5h5M20 20v-5h-5M20 9A8 8 0 006.3 5.3L4 8m16 8a8 8 0 01-13.7 3.7L4 17" />
  </svg>
);

const IconBanking: React.FC = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M12 3L3 8.25h18L12 3zM5.25 10.5v7.5m4.5-7.5v7.5m4.5-7.5v7.5m4.5-7.5v7.5M3 21h18" />
  </svg>
);

const IconDocuments: React.FC = () => (
  <svg className="sidebar-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M6 3h12v18l-3-2-3 2-3-2-3 2V3zm3 5h6m-6 4h6m-6 4h3" />
  </svg>
);

const menuIcons: Record<string, React.FC> = {
  '/dashboard': IconDashboard,
  '/workspace': IconWorkspace,
  '/offers': IconOffers,
  '/invoices': IconInvoices,
  '/recurring': IconRecurring,
  '/banking': IconBanking,
  '/documents': IconDocuments,
  '/users': IconUsers,
  '/api-tokens': IconKey,
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, collapsed, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const baseMenuItems = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/workspace', label: 'Kunden' },
    { path: '/offers', label: 'Angebote' },
    { path: '/invoices', label: 'Rechnungen' },
    { path: '/recurring', label: 'Serienrechnungen' },
    { path: '/banking', label: 'Konten' },
    { path: '/documents', label: 'Dokumente' },
  ];

  const adminMenuItems = [
    { path: '/users', label: 'User Management' },
    { path: '/api-tokens', label: 'API Tokens' },
  ];

  const menuItems = isAdmin() ? [...baseMenuItems, ...adminMenuItems] : baseMenuItems;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <aside
        className={`sidebar ${isOpen ? 'sidebar-open' : ''} ${collapsed ? 'sidebar-collapsed' : ''}`}
        aria-label="Main navigation"
      >
        <div className="sidebar-header">
          {!collapsed && (
            <Link to="/" className="flex items-center gap-2 sidebar-brand-link" onClick={onClose}>
              {brand.logo && (
                <img
                  src={brand.logo}
                  alt={brand.shortName}
                  style={{ width: 24, height: 24 }}
                  className="sidebar-logo"
                />
              )}
              <h2>{brand.shortName}</h2>
            </Link>
          )}
          <button className="sidebar-close" onClick={onClose} aria-label="Close navigation">
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <button
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <IconCollapse collapsed={collapsed} />
          </button>
        </div>
        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = menuIcons[item.path];
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                onClick={onClose}
                title={collapsed ? item.label : undefined}
              >
                <span className="sidebar-icon">{Icon && <Icon />}</span>
                {!collapsed && <span className="sidebar-label">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Theme toggle */}
        <div className="sidebar-theme-toggle">
          <button
            onClick={toggleTheme}
            className="user-menu-item"
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            title={collapsed ? (theme === 'light' ? 'Dark Mode' : 'Light Mode') : undefined}
          >
            <span className="user-menu-icon">
              {theme === 'light' ? <IconMoon /> : <IconSun />}
            </span>
            {!collapsed && <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>}
          </button>
        </div>

        {/* User Menu */}
        <div className="sidebar-footer">
          <div className="user-section">
            <div
              className="user-info"
              onClick={() => setShowUserMenu(!showUserMenu)}
              role="button"
              tabIndex={0}
              aria-expanded={showUserMenu}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowUserMenu(!showUserMenu); }}}
            >
              <div className="user-avatar" style={{ backgroundColor: avatarColor(user?.firstName, user?.lastName) }}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              {!collapsed && (
                <>
                  <div className="user-details">
                    <div className="user-name">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="user-role">
                      {user?.role === 'ADMIN' ? 'Administrator' : 'User'}
                    </div>
                  </div>
                  <div className="user-menu-toggle">
                    <IconChevron open={showUserMenu} />
                  </div>
                </>
              )}
            </div>

            {showUserMenu && (
              <div className="user-menu">
                <Link
                  to="/change-password"
                  className="user-menu-item"
                  onClick={() => { setShowUserMenu(false); onClose(); }}
                >
                  <span className="user-menu-icon"><IconKey /></span>
                  {!collapsed && <span>Change Password</span>}
                </Link>
                <button
                  className="user-menu-item"
                  onClick={handleLogout}
                >
                  <span className="user-menu-icon"><IconLogout /></span>
                  {!collapsed && <span>Sign Out</span>}
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
